import { Icon } from "@iconify/react";
import { TextInput, Button } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { useFetcher, useSearchParams } from "@remix-run/react";
import { useAtom } from "jotai";
import { useEffect, useMemo } from "react";

import { GeneralErrorBoundary } from "~/components/error-boundary";
import Logo from "~/components/layout/logo";
import { modalAtom } from "~/stores";

import { actionHandler } from "./action";
import { ForgotPasswordForm, forgotPasswordSchema } from "./constants";

export async function action({ request }: ActionFunctionArgs) {
  return actionHandler(request);
}

export const meta: MetaFunction = () => [{ title: "Forgot Password" }];

export default function ForgotPasswordPage() {
  const form = useForm<ForgotPasswordForm>({
    initialValues: {
      email: "",
    },
    validate: zodResolver(forgotPasswordSchema),
  });

  const fetcher = useFetcher<typeof action>();

  function requestResetPassword(values: typeof form.values) {
    const { hasErrors } = form.validate();
    if (!hasErrors) {
      fetcher.submit(
        { ...values },
        {
          method: "post",
          encType: "application/json",
          action: "/auth/forgot-password",
        },
      );
    }
  }
  const [searchParams, setSearchParams] = useSearchParams();
  const isModalOpen = useMemo(() => searchParams.get("modal"), [searchParams]);

  const [modal, setModal] = useAtom(modalAtom);
  useEffect(() => {
    if (isModalOpen === "success") {
      setModal({
        ...modal,
        isOpen: true,
        illustration: (
          <img src="/icons/publish.svg" alt="request reset password success" />
        ),
        title: "Permintaan Reset Password Berhasil",
        description: "Cek email anda untuk mengatur ulang password anda.",
        confirmText: "Okay",
        variant: "success",
        onConfirm: () => {
          searchParams.delete("modal");
          setSearchParams(searchParams);
        },
      });
    }

    if (isModalOpen === "error") {
      setModal({
        ...modal,
        isOpen: true,
        illustration: (
          <img src="/error.svg" alt="request reset password error" />
        ),
        title: "Gagal Mengirim Permintaan Reset Password",
        description: "Pastikan email dan cabang yang anda masukkan benar.",
        confirmText: "Coba Lagi",
        variant: "error",
        onConfirm: () => {
          searchParams.delete("modal");
          setSearchParams(searchParams);
        },
      });
    }
  }, [isModalOpen]);

  return (
    <div className="flex flex-col justify-center items-center gap-10 w-5/6 lg:w-fit">
      <div className="flex flex-col text-center w-full lg:w-full gap-10">
        <Logo className="w-[180px]" hideIhc={true} />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-left">
            Ganti kata sandi
          </h1>
          <p className="!text-[var(--mantine-color-dark-3)] text-left">
            Masukkan email yang terdaftar sebelumnya
          </p>
        </div>
      </div>
      <div className="flex flex-col w-full">
        <div className="w-full">
          <form
            onSubmit={form.onSubmit(requestResetPassword)}
            className="flex flex-col gap-3"
          >
            <TextInput
              label="Username"
              placeholder="Masukkan username"
              autoComplete="off"
              aria-label="email"
              withAsterisk
              data-cy="email"
              {...form.getInputProps("email")}
              leftSection={<Icon icon="tabler:user" className="h-4 w-4" />}
            />

            <Button
              type="submit"
              size="sm"
              color="ihcBlue"
              fullWidth
              className="mt-3"
              data-cy="submit"
              loading={fetcher.state === "submitting"}
            >
              Kirim
            </Button>
            <Button
              leftSection={
                <Icon icon="tabler:arrow-left" className="h-4 w-4" />
              }
              component="a"
              href="/auth/login"
              variant="default"
            >
              Kembali
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
